import * as React from "react"

import { cn } from "@/lib/utils"

// Symbol glyphs (⌘, ⇧, …) are announced poorly or not at all by screen
// readers, so map the common ones to readable names exposed via sr-only text.
const KEY_LABELS: Record<string, string> = {
  "⌘": "Command",
  "⌃": "Control",
  "⌥": "Option",
  "⇧": "Shift",
  "⎇": "Alt",
  "⏎": "Enter",
  "↵": "Enter",
  "⌫": "Backspace",
  "⌦": "Delete",
  "⎋": "Escape",
  "⇥": "Tab",
  "⇪": "Caps Lock",
  "␣": "Space",
  "↑": "Arrow Up",
  "↓": "Arrow Down",
  "←": "Arrow Left",
  "→": "Arrow Right",
}

export interface KbdProps extends React.HTMLAttributes<HTMLElement> {
  ref?: React.Ref<HTMLElement>
  children: React.ReactNode
}

function Kbd({ className, children, ref, ...props }: KbdProps) {
  const label = typeof children === "string" ? KEY_LABELS[children] : undefined

  return (
    <kbd
      ref={ref}
      className={cn(
        "inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-[3px] border border-dinnerplate-white bg-off-white text-grey text-[10px] font-inter font-medium leading-none select-none",
        className,
      )}
      {...props}
    >
      {label ? (
        <>
          <span className="sr-only">{label}</span>
          <span aria-hidden="true">{children}</span>
        </>
      ) : (
        children
      )}
    </kbd>
  )
}

export interface KbdShortcutProps extends React.HTMLAttributes<HTMLDivElement> {
  ref?: React.Ref<HTMLDivElement>
  keys: React.ReactNode[]
}

function KbdShortcut({ className, keys, ref, ...props }: KbdShortcutProps) {
  return (
    <div
      ref={ref}
      className={cn("inline-flex items-center gap-1", className)}
      {...props}
    >
      {keys.map((key, i) => (
        <Kbd key={`${String(key)}-${i}`}>{key}</Kbd>
      ))}
    </div>
  )
}

export { Kbd, KbdShortcut }
